import { getKeyboardFocusableElements, getElementFromEvent } from '../utils';
import BaseComponent from './BaseComponent';

export default class Menu extends BaseComponent {
	constructor(el) {
		super();

		this.header = el;
		this.hamburger = this.header.querySelector('[data-hamburger]');
		this.menu = this.header.querySelector('[data-menu]');
		this.menuLinks = this.header.querySelector('[data-nav-link');
		this.menuLeftWrapper = this.header.querySelector('[data-menu-left-wrapper]');
		this.menuCenterWrapper = this.header.querySelector('[data-menu-center-wrapper]');
		this.menuMainWrapper = this.header.querySelector('[data-menu-main-wrapper]');
		this.languageSwitcherWrapper = this.header.querySelector('[data-language-switcher-wrapper]');
		this.menuIsOpen = false;
		this.submenuBack = this.header.querySelector('[data-menu-back]');

		this.hamburger.addEventListener('click', () => {
			this.toggleMenu();
		});

		this.addListener(document, 'leave', (e) => {
			this.close();
		});

		this.submenuBack.addEventListener('click', () => {
			this.returnToMenu();
		});

		this.addListener(window, 'keydown', (e) => {
			if (e.key === 'Escape') {
				this.close();
			}

			if (this.menuIsOpen) {
				if (e.key === 'Tab' || e.key === 9) {
					if (e.shiftKey) {
						/* shift + tab */
						if (document.activeElement === this.firstElement) {
							this.lastElement.focus();
							e.preventDefault();
						}
					} /* tab */ else {
						if (document.activeElement === this.lastElement) {
							this.firstElement.focus();
							e.preventDefault();
						}
					}
				}
			}
		});

		this.resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				if (entry.contentRect.width > 992 && this.menuIsOpen) {
					this.close();
					this.menuIsOpen = false;
					document.dispatchEvent(new CustomEvent('enable-scroll'));
				}
			}
		});

		this.resizeObserver.observe(document.body);

		this.addListener(document, 'close-menu', (e) => {
			if (menu) {
				menu.close();
			}
		});

		this.addListener(document, 'click', (e) => {
			this.handleClickOutside(e);
		});

		if (this.menuIsOpen === false) {
			document.dispatchEvent(new CustomEvent('enable-scroll'));
		}

		let currentElem = null;

		this.menu.addEventListener('mouseover', (e) => {
			if (window.innerWidth < 992) {
				currentElem = null;
				return;
			}

			if (currentElem) {
				return;
			}

			let target = e.target.closest('.menu-page__link--has-children');

			if (!target) {
				return;
			}

			currentElem = target;

			const submenuToggle = target.querySelector('[data-submenu-toggle]');

			if (!submenuToggle) {
				return;
			}

			this.toggleSubmenu(submenuToggle);
		});

		this.menu.addEventListener('mouseout', (e) => {
			if (window.innerWidth < 992) {
				currentElem = null;
				return;
			}

			if (!currentElem) {
				return;
			}

			let relatedTarget = e.relatedTarget;

			while (relatedTarget) {
				if (relatedTarget == currentElem) {
					return;
				}

				relatedTarget = relatedTarget.parentNode;
			}

			this.menu.querySelectorAll('[data-submenu]').forEach((el) => {
				el.classList.remove('is-open');
				el.parentElement.querySelector('[data-submenu-toggle]').setAttribute('aria-expanded', false);
			});

			currentElem = null;
		});

		// Submenu toggle
		this.menu.addEventListener('click', (e) => {
			const submenuToggle = getElementFromEvent(e, '[data-submenu-toggle]');

			if (!submenuToggle) {
				return;
			}

			this.toggleSubmenu(submenuToggle);
		});

		// Language switcher
		this.menu.addEventListener('click', (e) => {
			const toggle = getElementFromEvent(e, '[data-language-menu-toggle]');

			if (!toggle) {
				return;
			}

			const languageSwitcherMenu = toggle.parentElement.querySelector('[data-language-menu]');

			if (!languageSwitcherMenu) {
				return;
			}

			if (languageSwitcherMenu.classList.contains('is-open')) {
				this.menu.classList.remove('lang-open');
				languageSwitcherMenu.classList.remove('is-open');
				toggle.setAttribute('aria-expanded', false);
			} else {
				this.menu.classList.add('lang-open');
				languageSwitcherMenu.classList.add('is-open');
				toggle.setAttribute('aria-expanded', true);
			}
		});
	}

	static init() {}

	toggleSubmenu(submenuToggle) {
		const submenu = submenuToggle.parentElement.querySelector('[data-submenu]');

		if (!submenu) {
			return;
		}

		this.menu.querySelectorAll('[data-submenu]').forEach((el) => {
			if (el !== submenu) {
				el.classList.remove('is-open');
				el.parentElement.querySelector('[data-submenu-toggle]').setAttribute('aria-expanded', false);
			}
		});

		if (submenu.classList.contains('is-open')) {
			submenu.classList.remove('is-open');
			submenuToggle.setAttribute('aria-expanded', false);
		} else {
			submenu.classList.add('is-open');
			submenuToggle.setAttribute('aria-expanded', true);
			this.menu.classList.add('submenu-open');
		}
	}

	isVisible(elem) {
		return elem.offsetWidth > 0 || elem.offsetHeight > 0 || elem.getClientRects().length > 0;
	}

	setKeyboardFocusableElements() {
		const visibleLinksArray = [];
		const focusableElements = getKeyboardFocusableElements(this.menuMainWrapper);

		focusableElements.forEach((elm) => {
			if (this.isVisible(elm)) {
				visibleLinksArray.push(elm);
			}
		});

		if (visibleLinksArray.length) {
			this.firstElement = visibleLinksArray[0];
			this.lastElement = visibleLinksArray[visibleLinksArray.length - 1];
		}
	}

	returnToMenu() {
		this.menu.classList.remove('submenu-open');
		this.menu.classList.remove('lang-open');

		this.menu.querySelectorAll('[data-submenu]').forEach((el) => {
			el.classList.remove('is-open');
			el.parentElement.querySelector('[data-submenu-toggle]').setAttribute('aria-expanded', false);
		});

		this.menu.querySelectorAll('[data-language-menu]').forEach((el) => {
			el.classList.remove('is-open');
			el.parentElement.querySelector('[data-language-menu-toggle]').setAttribute('aria-expanded', false);
		});
	}

	toggleMenu() {
		if (this.menuIsOpen) {
			this.close();
		} else {
			this.open();
		}
	}

	open() {
		if (!window.menuInteractive) {
			return;
		}

		this.menu.classList.add('is-open');
		this.header.classList.add('is-open');
		this.menuIsOpen = true;
		this.hamburger.setAttribute('aria-expanded', true);
		this.hamburger.setAttribute('aria-label', 'Sluit het menu');

		this.setKeyboardFocusableElements();
		this.firstElement.focus();

		if (window.innerWidth < 992) {
			document.dispatchEvent(
				new CustomEvent('disable-scroll', {
					detail: {
						scrollingElm: this.menuMainWrapper,
					},
				})
			);
		}
	}

	close() {
		this.menu.classList.remove('is-open');
		this.header.classList.remove('is-open');
		this.menuIsOpen = false;
		this.hamburger.setAttribute('aria-expanded', false);
		this.hamburger.setAttribute('aria-label', 'Open menu');
		this.menu.classList.remove('submenu-open');
		this.menu.classList.remove('lang-open');

		this.menu.querySelectorAll('[data-submenu]').forEach((el) => {
			el.classList.remove('is-open');
			el.parentElement.querySelector('[data-submenu-toggle]').setAttribute('aria-expanded', false);
		});

		this.menu.querySelectorAll('[data-language-menu]').forEach((el) => {
			el.classList.remove('is-open');
			el.parentElement.querySelector('[data-language-menu-toggle]').setAttribute('aria-expanded', false);
		});

		document.dispatchEvent(new CustomEvent('enable-scroll'));
	}

	handleClickOutside(event) {
		if (this.menuIsOpen && window.innerWidth > 992) {
			if (!this.menu.contains(event.target) && event.target !== this.hamburger) {
				this.close();
			}
		}
	}

	destroy() {
		super.destroy();
		this.resizeObserver.disconnect();
	}
}
